// Two transports ship by default:
//   console  — always on (formatted; respects level filter)
//   sentry   — registered post-Sentry-init; info+ becomes a breadcrumb, error
//              becomes a captured event

import * as Sentry from '@sentry/vue'
import type { LogRecord, Transport } from './types'

const formatPrefix = (record: LogRecord): string => {
  const scope = record.scope ? ` (${record.scope})` : ''
  return `[${record.timestamp}] [${record.level.toUpperCase()}]${scope}`
}

const toReadable = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    // Circular structures — fall back to the live object.
    return value
  }
}

export const consoleTransport: Transport = {
  name: 'console',
  write(record) {
    const prefix = formatPrefix(record)
    const args: unknown[] = [prefix, record.message]
    // Plain objects (AppError, context) printed as "[object Object]" / collapsed
    // previews in some consoles — stringify them; real Errors keep their stack (BUG-022).
    if (record.context && Object.keys(record.context).length) args.push(toReadable(record.context))
    if (record.error) {
      args.push(record.error instanceof Error ? record.error : toReadable(record.error))
    }

    switch (record.level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.debug(...args)
        break
      case 'info':
        // eslint-disable-next-line no-console
        console.info(...args)
        break
      case 'warn':
        console.warn(...args)
        break
      case 'error':
        console.error(...args)
        break
    }
  },
}

export const sentryTransport: Transport = {
  name: 'sentry',
  write(record) {
    if (record.level === 'debug') return

    const sentryLevel = record.level === 'warn' ? 'warning' : record.level
    Sentry.addBreadcrumb({
      category: record.scope ?? 'app',
      level: sentryLevel,
      message: record.message,
      data: record.context as Record<string, unknown> | undefined,
    })

    if (record.level === 'error') {
      if (record.error instanceof Error) {
        Sentry.captureException(record.error, {
          extra: { message: record.message, ...record.context },
        })
      } else {
        Sentry.captureMessage(record.message, {
          level: 'error',
          extra: { error: record.error, ...record.context },
        })
      }
    }
  },
}
